import * as React from 'react'

import { renderers } from '@markdoc/markdoc'
import type { HeadersFunction, LoaderFunctionArgs, MetaFunction } from 'react-router'
import { redirect, useLoaderData } from 'react-router'

import ContentImage from '@/components/content/ContentImage'
import { findAllChapters, findCourse } from '@/models/course.server'
import { pageHelper } from '@/request'
import { formatDurationISO8601 } from '@/utils/number'

type LoaderData = typeof loader

export async function loader({ params, url }: LoaderFunctionArgs) {
  const course = findCourse(params.course as string)

  if (!course) {
    throw new Response('Not Found', { status: 404 })
  }

  if (course.v !== 2) {
    const chapters = findAllChapters(params.course as string)

    if (chapters.length > 0) {
      return redirect(chapters[0].path)
    }
  }

  const { root } = pageHelper(url)

  return {
    course: course,
    root: root
  }
}

export const headers: HeadersFunction = ({ parentHeaders }) => ({
  'Cache-Control': parentHeaders.get('Cache-Control') ?? ''
})

export const meta: MetaFunction<LoaderData> = ({ loaderData }) => {
  if (!loaderData) return []

  const {
    course,
    course: { meta },
    root
  } = loaderData

  const title = `${meta.title || course.name} — PlanetScale`
  const description = meta.description || course.description
  const image = `${root}${meta.image}`

  return [
    { title },
    { name: 'description', content: description },
    { name: 'robots', content: meta.robots },
    { property: 'og:url', content: `https://planetscale.com${course.path}` },
    { property: 'og:type', content: 'website' },
    { property: 'og:title', content: title },
    { property: 'og:image', content: image },
    { property: 'og:description', content: description },
    { property: 'twitter:card', content: 'summary_large_image' },
    { property: 'twitter:site', content: '@planetscale' },
    { property: 'twitter:creator', content: '@planetscale' },
    { property: 'twitter:url', content: `https://planetscale.com${course.path}` },
    { property: 'twitter:title', content: title },
    { property: 'twitter:description', content: description },
    { property: 'twitter:image', content: image },
    { tagName: 'link', rel: 'canonical', href: `https://planetscale.com${course.path}` },
    {
      'script:ld+json': {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: course.name,
        articleBody: course.body_text,
        image: `https://planetscale.com${meta.image}`,
        dateCreated: course.createdAt,
        datePublished: course.createdAt,
        dateModified: course.updatedAt,
        author: {
          '@type': 'Organization',
          name: 'PlanetScale',
          url: 'planetscale.com'
        }
      }
    },
    {
      'script:ld+json': {
        '@context': 'https://schema.org',
        '@type': 'Course',
        name: course.name,
        description: course.description,
        image: `https://planetscale.com${meta.image}`,
        datePublished: course.createdAt,
        inLanguage: 'en',
        provider: {
          '@type': 'Organization',
          name: 'PlanetScale',
          url: 'planetscale.com'
        },
        offers: {
          '@type': 'Offer',
          category: 'Free',
          price: '0',
          priceCurrency: 'USD'
        },
        hasCourseInstance: [
          {
            '@type': 'CourseInstance',
            courseMode: ['Online'],
            courseWorkload: formatDurationISO8601(course.duration),
            instructor: {
              '@type': 'Person',
              jobTitle: course.instructor.title,
              name: course.instructor.name
            }
          }
        ]
      }
    }
  ]
}

export default function Course() {
  const { course } = useLoaderData<LoaderData>()

  return (
    <main className='mx-auto flex max-w-(--breakpoint-lg) flex-col gap-5 px-4 sm:px-5 lg:px-6'>
      <div className='course-overview prose prose-invert prose-pre:bg-secondary'>
        {renderers.react(course.body, React, {
          components: {
            ContentImage
          }
        })}
      </div>

      <div className='mb-2'>
        <p className='text-primary'>
          Feedback or questions? Reach out to our team at{' '}
          <a href='mailto:education@planetscale.com'>education@planetscale.com</a>.
        </p>
      </div>
    </main>
  )
}
